import '@app/App.css';
import { Button } from "@comps/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@comps/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@comps/ui/form";
import { Input } from "@comps/ui/input";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { ObjectInfos } from "@dto/objects";
import { FullRightAccess, hasGlobalRightAccess, ViewRightType } from "@dto/Users";
import { View } from "@dto/views";
import { zodResolver } from "@hookform/resolvers/zod";
import { BASE_URL, DEFAULT_ORDER_INCREMENT } from "@utils/constants";
import { copyViewLocalSettings } from "@utils/localStorage";
import { getWsButtonStyle, TW_AppDialogButton, TW_ButtonList, TW_ErrorMessage, TW_ViewMenuItem, TW_ViewMenuItemIcon, useHover } from "@utils/twClasses";
import { post } from "@utils/utils";
import { Copy } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useModelStore, useUserStore } from "src/store";
import { z } from "zod";

interface ViewMenuDuplicateItemProps {
  objectInfos: ObjectInfos;
  view: View;
  onViewUpdate: (view: View) => void;
  closeMenu: () => void;
}

function ViewMenuDuplicateItem({ objectInfos, view, onViewUpdate, closeMenu }: ViewMenuDuplicateItemProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const buttonHover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor));  
  const user = useUserStore(state => state.user);
  const userRole = useModelStore(state => state.userRole);

  const hasViewFullRight = hasGlobalRightAccess(user, userRole, ViewRightType, FullRightAccess);

  const viewUpdated = (view: View) => {
    setIsDialogOpen(false);
    closeMenu();
    onViewUpdate(view);
  }

  function cancel(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    setIsDialogOpen(false);
    closeMenu();
  }

  function click(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    if (!hasViewFullRight) {
      return;
    }
    setIsDialogOpen(true);
  }

  function onOpenChange(open: boolean) {
    setIsDialogOpen(open);
    if (!open) {
      closeMenu();
    }
  }


  const disabled = view === null || !hasViewFullRight;
  return (
    <Dialog open={isDialogOpen} onOpenChange={onOpenChange}>
      <DialogTrigger className={TW_ViewMenuItem + ` ${disabled ? "item-disabled" : ""}`} {...buttonHover} onClick={click}><Copy className={TW_ViewMenuItemIcon} />{t("view.button.duplicate.text")}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("view.dialog.duplicate.title")}</DialogTitle>
          <DialogDescription className="hidden">{t("object.dialog.duplicate.description")}</DialogDescription>
        </DialogHeader>
        {view && <ViewDuplicateForm objectInfos={objectInfos} initialView={view} onViewUpdate={viewUpdated} onCancel={cancel} />}
      </DialogContent>
    </Dialog>
  )
}

interface ViewDuplicateFormProps {
  objectInfos: ObjectInfos;
  initialView: View;
  onViewUpdate: (view: View) => void;
  onCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

function ViewDuplicateForm({ objectInfos, initialView, onViewUpdate, onCancel }: ViewDuplicateFormProps) {

  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const [error, setError] = useState<string | null>(null);
  const wsColor = useModelStore(state => state.wsColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const okHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));

  const formSchema = z.object({
    viewName: z.string().min(1, {
      message: t("view.dialog.error.name.missing"),
    }),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      viewName: initialView.name + t("view.dialog.duplicate.suffix"),
    },
  })

  function onSubmit(values: z.infer<typeof formSchema>) {
    if (!workspace) {
      console.error("No workspace selected");
      return;
    }
    let order = 0;
    let views = objectInfos.views;
    let originalIndex = views.findIndex(v => v._key === initialView._key);
    if (originalIndex === -1) {
      console.error("View not found");
      return;
    }

    if (originalIndex === views.length - 1) {
      order = (views[originalIndex].order ?? 0) + DEFAULT_ORDER_INCREMENT;
    } else {
      order = ((views[originalIndex].order ?? 0) + (views[originalIndex + 1].order ?? 0)) / 2;
    }

    // let newIndex = originalIndex + 1;
    let newView = { ...initialView, name: values.viewName, order: order };
    // views.splice(newIndex, 0, newView);

    // const body = {
    //   name: values.viewName,
    //   order: order,
    // }
    post<View>(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${objectInfos.object._key}/views`, newView).then(response => {
      if (response) {
        if (isCompozzError(response)) {
          const cError = response as CompozzError;
          if (cError.i18n) {
            setError(translate(t, cError.i18n, cError.params));
          } else {
            setError(cError.message);
          }
        } else {
          setError(null);
          const duplicatedView = response as View;
          // The column widths and the selected pivot period only live in
          // this browser: carry them over before the duplicate gets selected
          copyViewLocalSettings(objectInfos.object._key, initialView._key, duplicatedView._key);
          onViewUpdate(duplicatedView);
        }
      }
    })
  }

  function onEnterKeyPress(event: React.KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      form.handleSubmit(onSubmit)();
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="viewName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("view.dialog.duplicate.label")}</FormLabel>
              <FormControl>
                <Input placeholder={t("view.dialog.duplicate.placeholder")} {...field} onKeyDown={onEnterKeyPress} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {error && <div className={TW_ErrorMessage}>{error}</div>}
        {!error && <div className={TW_ErrorMessage}></div>}
        <div className={TW_ButtonList}>
          <Button className={TW_AppDialogButton} onClick={onCancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
          <Button className={TW_AppDialogButton} type="submit" {...okHover}>{t("general.button.confirm.text")}</Button>
        </div>
      </form>
    </Form>
  )
}

export default ViewMenuDuplicateItem;
